import { useState, useEffect } from "react";
import { AGRO_API_KEY, convertTempKelvinToCelsius } from "../../utils/Helpers";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { NDVIData, SoilData, WeatherData } from "../../types/agroTypes";
import { periods } from "../../utils/Options";
import { AgroMHistoryCardProps } from "../../types/propsTypes";

const WeatherDisplay = ({ dataHistory }: { dataHistory: WeatherData[] }) => {
    const transformedData = dataHistory.map(item => ({
        date: new Date(item.dt * 1000).toLocaleDateString(),
        temp: convertTempKelvinToCelsius(item.main.temp),
        pressure: item.main.pressure,
        humidity: item.main.humidity
    }));
    return (
        <div className="flex flex-col w-full rounded-lg">
            <h2 className="text-sm font-semibold text-slate-500 uppercase py-1 px-1 border-b border-slate-500 mb-3 mx-2">Weather Forecast</h2>
            <ResponsiveContainer width="100%" height={240}>
                <LineChart data={transformedData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="temp" stroke="#82ca9d" strokeWidth={2} />
                    <Line type="monotone" dataKey="pressure" stroke="#8884d8" strokeWidth={2} />
                    <Line type="monotone" dataKey="humidity" stroke="#ff7300" strokeWidth={2} />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

const SoilDisplay = ({ dataHistory, period, setPeriod }: { dataHistory: SoilData[]; period: number; setPeriod: (period: number) => void; }) => {
    const transformedData = dataHistory.map(item => ({
        date: new Date(item.dt * 1000).toLocaleDateString(),
        temp: convertTempKelvinToCelsius(item.t10),
        moisture: item.moisture
    }));
    return (
        <>
            <div className="flex flex-col w-full rounded-lg">
                <h2 className="text-sm font-semibold text-slate-500 uppercase py-1 px-1 border-b border-slate-500 mb-3 mx-2">Soil History</h2>
                <div className="mx-3 flex justify-end mb-2">
                    <select className="h-10 w-[200px]" value={period} onChange={(e) => setPeriod(Number(e.target.value))}>
                        <option selected className="text-slate-500" value={""}>Select Period</option>
                        {periods.map((d, index) => (
                            <option selected className="text-slate-500" key={index} value={d.value}>{d.description}</option>
                        ))}
                    </select>
                </div>
                <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={transformedData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="temp" stroke="#82ca9d" strokeWidth={2} />
                        <Line type="monotone" dataKey="moisture" stroke="#8884d8" strokeWidth={2} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </>
    );
};

const NDVIDisplay = ({ dataHistory, period, setPeriod }: { dataHistory: NDVIData[]; period: number; setPeriod: (period: number) => void; }) => {
    const transformedData = dataHistory.map(item => ({
        date: new Date(item.dt * 1000).toLocaleDateString(),
        mean: item.data.mean,
        median: item.data.median,
        max: item.data.max,
        min: item.data.min
    }));
    return (
        <div className="flex flex-col w-full rounded-lg">
            <h2 className="text-sm font-semibold text-slate-500 uppercase py-1 px-1 border-b border-slate-500 mb-3 mx-2">NDVI History</h2>
            <div className="mx-3 flex justify-end mb-2">
                <select className="h-10 w-[200px] border border-slate-400 rounded-md p-1" value={period} onChange={(e) => setPeriod(Number(e.target.value))}>
                    <option selected className="text-slate-500" value={""}>Select Period</option>
                    {periods.map((d, index) => (
                        <option selected className="text-slate-500" key={index} value={d.value}>{d.description}</option>
                    ))}
                </select>
            </div>
            <ResponsiveContainer width="100%" height={240}>
                <LineChart data={transformedData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="mean" stroke="#82ca9d" strokeWidth={2} />
                    <Line type="monotone" dataKey="median" stroke="#8884d8" strokeWidth={2} />
                    <Line type="monotone" dataKey="max" stroke="#ff7300" strokeWidth={2} />
                    <Line type="monotone" dataKey="min" stroke="#ff0000" strokeWidth={2} />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

const AgroMHistoryCard = ({ lat, lng, polygonId, type }: AgroMHistoryCardProps) => {
    const [data, setData] = useState<WeatherData[]>([]);
    const [soilData, setSoilData] = useState<SoilData[]>([])
    const [ndviData, setNdviData] = useState<NDVIData[]>([])
    const [period, setPeriod] = useState<number>(7);

    useEffect(() => {
        fetchCurrentWeather()
        fetchCurrentSoil()
        fetchCurrentNdvi()
    }, [])

    useEffect(() => {
        fetchCurrentWeather()
        fetchCurrentSoil()
        fetchCurrentNdvi()
    }, [period])

    const fetchCurrentWeather = async () => {
        try {
            const polyResponse = await fetch(`https://api.agromonitoring.com/agro/1.0/weather/forecast?lat=${lat}&lon=${lng}&appid=${AGRO_API_KEY}`, {
                method: "GET",
                headers: { "Content-Type": "application/json" }
            });
            if (!polyResponse.ok) {
                throw new Error(`API Error: ${polyResponse.status} ${polyResponse.statusText}`);
            }
            const weatherData: WeatherData[] = await polyResponse.json();
            weatherData.sort((a, b) => a.dt - b.dt);
            setData(weatherData)
        } catch (error) {
            console.error("Error fetching NDVI data:", error);
        }
    };

    const fetchCurrentSoil = async () => {
        const startDate = Math.floor((Date.now() - period * 24 * 60 * 60 * 1000) / 1000);
        const endDate = Math.floor(Date.now() / 1000);
        try {
            const polyResponse = await fetch(`https://api.agromonitoring.com/agro/1.0/soil/history?start=${startDate}&end=${endDate}&polyid=${polygonId}&appid=${AGRO_API_KEY}`, {
                method: "GET",
                headers: { "Content-Type": "application/json" }
            });
            if (!polyResponse.ok) {
                throw new Error(`API Error: ${polyResponse.status} ${polyResponse.statusText}`);
            }
            const soilData: SoilData[] = await polyResponse.json();
            soilData.sort((a, b) => a.dt - b.dt);
            setSoilData(soilData)
        } catch (error) {
            console.error("Error fetching NDVI data:", error);
        }
    };

    const fetchCurrentNdvi = async () => {
        const startDate = Math.floor((Date.now() - period * 24 * 60 * 60 * 1000) / 1000);
        const endDate = Math.floor(Date.now() / 1000);
        try {
            const polyResponse = await fetch(`https://api.agromonitoring.com/agro/1.0/ndvi/history?start=${startDate}&end=${endDate}&polyid=${polygonId}&appid=${AGRO_API_KEY}`, {
                method: "GET",
                headers: { "Content-Type": "application/json" }
            });
            if (!polyResponse.ok) {
                throw new Error(`API Error: ${polyResponse.status} ${polyResponse.statusText}`);
            }
            const ndviHistoryData: NDVIData[] = await polyResponse.json();
            ndviHistoryData.sort((a, b) => a.dt - b.dt);
            setNdviData(ndviHistoryData)
        } catch (error) {
            console.error("Error fetching NDVI data:", error);
        }
    };
    return (
        <div
            className="flex flex-col w-full rounded-xl items-center mt-8"
            style={{ scrollbarWidth: "thin" }}>
            <div
                className="flex flex-col flex-1 items-center justify-center w-full rounded-xl drop-shadow-sm overflow-y-scroll">
                {data && type === 'weather' && <WeatherDisplay dataHistory={data} />}

                {ndviData && type === 'nvdi' && <NDVIDisplay dataHistory={ndviData} period={period} setPeriod={setPeriod} />}

                {soilData && type === 'soil' && <SoilDisplay dataHistory={soilData} period={period} setPeriod={setPeriod} />}

            </div>
        </div>
    );
};

export default AgroMHistoryCard;
